import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Form } from 'antd';
import { Input } from 'antd';
import dayjs from 'dayjs';
import { DATE_RANGE_DEFAULT_VALUES } from '@/constants/date-time';
import { cn } from '@/libs/utils';
import { HR_FILTER_FORM_DEFAULT_PARAMS } from '@/shared/hr-requests';
import { DATE_FORMATS, formatDateForBackend } from '@/utils/formUtils';

export default function HRDocumentsFilterForm({
  form,
  queryParams,
  className,
  setQueryParams,
  onClose,
}) {
  const { t } = useTranslation(['forms', 'messages', 'tables']);

  const initialValues = useMemo(() => {
    return {
      createdAt: queryParams?.createdAt
        ? queryParams?.createdAt?.map((date) => dayjs(date))
        : [],
      updatedAt: queryParams?.updatedAt
        ? queryParams?.updatedAt?.map((date) => dayjs(date))
        : [],
      datesFromTo: queryParams?.datesFromTo
        ? queryParams?.datesFromTo?.map((date) => dayjs(date))
        : [],
      numOfDays: queryParams?.numOfDays ?? undefined,
    };
  }, [queryParams]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const handleClose = () => {
    onClose();
  };

  const handleReset = () => {
    setQueryParams(HR_FILTER_FORM_DEFAULT_PARAMS);

    form.resetFields();

    handleClose();
  };

  const onChangePicker = (name, dates) => {
    if (dates) {
      const updatedDates = [
        dates[0]?.startOf('day').format(DATE_FORMATS.DISPLAY_WITH_TIME),
        dates[1]?.endOf('day').format(DATE_FORMATS.DISPLAY_WITH_TIME),
      ];
      form.setFieldsValue({ name: updatedDates.map((date) => dayjs(date)) });
    } else {
      form.setFieldsValue({ name: null });
    }
  };

  const onFinish = async (values) => {
    const formValues = { ...values };

    // * Created At
    if (formValues?.createdAt) {
      formValues.createdAt = formValues?.createdAt.map((date) =>
        formatDateForBackend(date)
      );
    }

    if (formValues?.createdAt?.length < 1) {
      delete formValues.createdAt;
    }

    // * Updated At
    if (formValues?.updatedAt) {
      formValues.updatedAt = formValues?.updatedAt.map((date) =>
        formatDateForBackend(date)
      );
    }

    if (formValues?.updatedAt?.length < 1) {
      delete formValues.updatedAt;
    }

    // * Number of Days
    if (formValues?.numOfDays < 1) {
      formValues.numOfDays = undefined;
    }

    // * Dates From To
    if (formValues?.datesFromTo) {
      formValues.datesFromTo = formValues?.datesFromTo.map((date) =>
        formatDateForBackend(date)
      );
    }

    if (formValues?.datesFromTo?.length < 1) {
      delete formValues.datesFromTo;
    }

    if (Object.keys(formValues)?.length > 0) {
      formValues.offset = 1;

      setQueryParams(formValues);
    }

    handleClose();
  };

  return (
    <Form
      form={form}
      name="hr-requests_filter_form"
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form p-4', className)}
    >
      <Form.Item label={t('createdAt', { ns: 'tables' })} name="createdAt">
        <DatePicker.RangePicker
          className="ant-range-picker w-full"
          format={DATE_FORMATS.DISPLAY_WITH_TIME}
          showTime={{
            format: DATE_FORMATS.TIME_ONLY,
            defaultValue: DATE_RANGE_DEFAULT_VALUES,
          }}
          placeholder={[
            t('startDate', { ns: 'forms' }),
            t('endDate', { ns: 'forms' }),
          ]}
          onChange={(dates) => {
            onChangePicker('createdAt', dates);
          }}
        />
      </Form.Item>

      <Form.Item label={t('updatedAt', { ns: 'tables' })} name="updatedAt">
        <DatePicker.RangePicker
          className="ant-range-picker w-full"
          format={DATE_FORMATS.DISPLAY_WITH_TIME}
          showTime={{
            format: DATE_FORMATS.TIME_ONLY,
            defaultValue: DATE_RANGE_DEFAULT_VALUES,
          }}
          placeholder={[
            t('startDate', { ns: 'forms' }),
            t('endDate', { ns: 'forms' }),
          ]}
          onChange={(dates) => {
            onChangePicker('updatedAt', dates);
          }}
        />
      </Form.Item>

      <Form.Item label={t('datesFromTo', { ns: 'forms' })} name="datesFromTo">
        <DatePicker.RangePicker
          className="ant-range-picker w-full"
          format={DATE_FORMATS.DISPLAY_WITH_TIME}
          showTime={{
            format: DATE_FORMATS.TIME_ONLY,
            defaultValue: DATE_RANGE_DEFAULT_VALUES,
          }}
          placeholder={[
            t('startDate', { ns: 'forms' }),
            t('endDate', { ns: 'forms' }),
          ]}
          onChange={(dates) => {
            onChangePicker('datesFromTo', dates);
          }}
        />
      </Form.Item>

      <Form.Item
        label={t('numberOfDays', { ns: 'forms' })}
        name="numOfDays"
        className="w-full"
      >
        <Input type="number" placeholder={t('numberOfDays', { ns: 'forms' })} />
      </Form.Item>

      <div className="form-footer form-footer--sticky">
        <div className="flex gap-2">
          <Button
            type="default"
            className="btn-default btn-default--hover"
            onClick={handleReset}
          >
            {t('reset')}
          </Button>

          <Button type="primary" htmlType="submit" className="btn-primary">
            {t('submit')}
          </Button>
        </div>
      </div>
    </Form>
  );
}
